import { useCallback, useEffect, useState } from "react";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import { n2 } from "../../lib/format";
import { Modal, MoneyInput, Notice, Pager, parseMoney } from "../../lib/ui";

/*
 * Record & verify cash receipts – port of ReceiveCash.php (API: /api/cash-receipts...)
 * Record a Service, Prepayment or Capital payment; verifying posts Dr Cash 1000 / Bank 1010 and
 * Cr Capital 3000 / Revenue 4000 / 2500 (prepayment).
 */

const BLANK = () => ({ payment_date: new Date().toISOString().slice(0, 10), payment_amount: "", payment_method: "Cash", payment_class: "", service_id: "", client_id: "", payment_details: "" });

export default function CashReceipts() {
  const api = useApi();
  const [f, setF] = useState(BLANK);
  const [clients, setClients] = useState([]);
  const [list, setList] = useState(null);
  const [page, setPage] = useState(1);
  const [notice, setNotice] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const load = useCallback(async (p) => { try { setList(await api.get("/api/cash-receipts", { page: p })); } catch (e) { setNotice({ tone: "err", text: e.message }); } }, [api]);
  useEffect(() => { load(page); }, [load, page]);
  useEffect(() => { api.get("/api/clients").then(setClients).catch(() => {}); }, [api]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    try { const r = await api.post("/api/cash-receipts", { ...f, payment_amount: parseMoney(f.payment_amount) }); setNotice({ tone: "ok", text: r.message }); setF(BLANK()); load(page); }
    catch (err) { setNotice({ tone: "err", text: err.message }); }
  };
  const act = async () => {
    const { kind, row } = confirm; setConfirm(null);
    try {
      const r = kind === "verify" ? await api.post(`/api/cash-receipts/${row.payment_id}/verify`, {}) : await api.request(`/api/cash-receipts/${row.payment_id}`, "DELETE");
      setNotice({ tone: "ok", text: r.message }); load(page);
    } catch (e) { setNotice({ tone: "err", text: e.message }); }
  };

  return (
    <main className="pg">
      <PageHeader slug="ReceiveCash" />
      <Notice notice={notice} onClose={() => setNotice(null)} />
      <section className="panel" style={{ marginBottom: "1.25rem" }}>
        <form className="form" onSubmit={save} style={{ maxWidth: "none" }}>
          <div className="field"><label htmlFor="rc-d">Payment date</label><input id="rc-d" type="date" required value={f.payment_date} onChange={set("payment_date")} /></div>
          <div className="field"><label htmlFor="rc-a">Amount (TZS)</label><MoneyInput id="rc-a" required value={f.payment_amount} onChange={(v) => setF({ ...f, payment_amount: v })} /></div>
          <div className="field"><label htmlFor="rc-m">Method</label><select id="rc-m" value={f.payment_method} onChange={set("payment_method")}><option>Cash</option><option>Bank Transfer</option></select></div>
          <div className="field"><label htmlFor="rc-c">Class</label><select id="rc-c" required value={f.payment_class} onChange={set("payment_class")}>
            <option value="">Select class</option><option>Service</option><option>Prepayment</option><option>Capital</option></select></div>
          <div className="field"><label htmlFor="rc-s">Service</label><select id="rc-s" required value={f.service_id} onChange={set("service_id")}>
            <option value="">Select service</option>{(list?.services || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
          <div className="field"><label htmlFor="rc-cl">Client</label><select id="rc-cl" required value={f.client_id} onChange={set("client_id")}>
            <option value="">Select client</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
          <div className="field wide"><label htmlFor="rc-det">Payment details</label><textarea id="rc-det" required rows={3} value={f.payment_details} onChange={set("payment_details")} /></div>
          <div className="form-actions"><button type="submit" className="btn primary"><i className="fas fa-floppy-disk" /> Register payment</button></div>
        </form>
      </section>
      <section className="panel">
        <div style={{ padding: "1rem 1.25rem", borderBottom: "1px solid var(--line)" }}><h2 style={{ fontSize: "1rem", margin: 0 }}>Recorded, not yet verified</h2></div>
        <div className="tbl-wrap"><table className="tbl">
          <thead><tr><th>Date</th><th>Client</th><th>Service</th><th className="num">Amount</th><th>Method</th><th>Class</th><th /></tr></thead>
          <tbody>{list?.rows.map((r) => (
            <tr key={r.payment_id}><td>{r.payment_date}</td><td style={{ fontWeight: 600, color: "var(--ink)" }}>{r.client_name}</td><td>{r.service_name || "–"}</td>
              <td className="num mono">{n2(r.payment_amount)}</td><td>{r.payment_method}</td><td><span className="pill-tag">{r.payment_class}</span></td>
              <td style={{ whiteSpace: "nowrap" }}><button type="button" className="btn sm primary" onClick={() => setConfirm({ kind: "verify", row: r })}>Verify</button>{" "}
                <button type="button" className="btn sm" onClick={() => setConfirm({ kind: "delete", row: r })}>Delete</button></td></tr>))}</tbody>
        </table>{list && list.rows.length === 0 && <div className="empty"><i className="fas fa-inbox" />Nothing waiting for verification.</div>}</div>
        {list && <Pager page={list.page} totalPages={list.pages} onPage={setPage} />}
      </section>
      {confirm && (
        <Modal title={confirm.kind === "verify" ? "Verify payment?" : "Delete payment?"} onClose={() => setConfirm(null)}
          actions={<><button type="button" className="btn" onClick={() => setConfirm(null)}>Cancel</button>
            <button type="button" className={`btn ${confirm.kind === "verify" ? "primary" : "danger"}`} onClick={act}>{confirm.kind === "verify" ? "Verify" : "Delete"}</button></>}>
          {confirm.row.payment_class} payment of TZS {n2(confirm.row.payment_amount)} from {confirm.row.client_name}.
          {confirm.kind === "verify" ? " This posts it to the ledger and can't be undone." : " This can't be undone."}
        </Modal>
      )}
    </main>
  );
}
